import React from "react";
import { FaBriefcase, FaBuilding, FaChartLine, FaCheck, FaRocket } from "react-icons/fa";
import { formatInr } from "../data/subscriptionPlans";

const PLAN_ICONS = {
  ONE_MONTH: FaRocket,
  THREE_MONTH: FaChartLine,
  SIX_MONTH: FaBriefcase,
  ONE_YEAR: FaBuilding,
};

const TONE_STYLES = {
  slate: {
    bar: "bg-slate-700",
    surface: "bg-slate-50",
    darkSurface: "bg-slate-800/70",
    text: "text-slate-700",
    darkText: "text-slate-300",
    border: "border-slate-600",
    darkBorder: "border-slate-500",
    selected: "border-slate-700",
    darkSelected: "border-slate-400",
    action: "bg-slate-800 text-white hover:bg-slate-900",
    darkAction: "bg-slate-700 text-white hover:bg-slate-600",
    outline: "border-slate-300 text-slate-800 hover:border-slate-700 hover:bg-slate-50",
    darkOutline: "border-slate-600 text-slate-100 hover:border-slate-400 hover:bg-slate-800",
  },
  blue: {
    bar: "bg-blue-600",
    surface: "bg-blue-50",
    darkSurface: "bg-blue-950/40",
    text: "text-blue-700",
    darkText: "text-blue-300",
    border: "border-blue-600",
    darkBorder: "border-blue-400",
    selected: "border-blue-600",
    darkSelected: "border-blue-400",
    action: "bg-blue-600 text-white hover:bg-blue-700",
    darkAction: "bg-blue-600 text-white hover:bg-blue-500",
    outline: "border-blue-200 text-blue-800 hover:border-blue-600 hover:bg-blue-50",
    darkOutline: "border-blue-800 text-blue-200 hover:border-blue-400 hover:bg-blue-950/50",
  },
  teal: {
    bar: "bg-teal-600",
    surface: "bg-teal-50",
    darkSurface: "bg-teal-950/40",
    text: "text-teal-700",
    darkText: "text-teal-300",
    border: "border-teal-600",
    darkBorder: "border-teal-400",
    selected: "border-teal-600",
    darkSelected: "border-teal-400",
    action: "bg-teal-700 text-white hover:bg-teal-800",
    darkAction: "bg-teal-600 text-white hover:bg-teal-500",
    outline: "border-teal-200 text-teal-800 hover:border-teal-600 hover:bg-teal-50",
    darkOutline: "border-teal-800 text-teal-200 hover:border-teal-400 hover:bg-teal-950/50",
  },
  indigo: {
    bar: "bg-indigo-600",
    surface: "bg-indigo-50",
    darkSurface: "bg-indigo-950/40",
    text: "text-indigo-700",
    darkText: "text-indigo-300",
    border: "border-indigo-600",
    darkBorder: "border-indigo-400",
    selected: "border-indigo-600",
    darkSelected: "border-indigo-400",
    action: "bg-indigo-600 text-white hover:bg-indigo-700",
    darkAction: "bg-indigo-600 text-white hover:bg-indigo-500",
    outline: "border-indigo-200 text-indigo-800 hover:border-indigo-600 hover:bg-indigo-50",
    darkOutline: "border-indigo-800 text-indigo-200 hover:border-indigo-400 hover:bg-indigo-950/50",
  },
};

const SubscriptionPlanCard = ({ plan, selected, onSelect, isDarkMode = false }) => {
  const tone = TONE_STYLES[plan.tone] || TONE_STYLES.teal;
  const headingClass = isDarkMode ? "text-slate-100" : "text-slate-950";
  const bodyClass = isDarkMode ? "text-slate-300" : "text-slate-600";
  const mutedClass = isDarkMode ? "text-slate-400" : "text-slate-500";
  const dividerClass = isDarkMode ? "border-slate-700" : "border-slate-200";
  const cardClass = selected
    ? `${isDarkMode ? tone.darkSelected : tone.selected} ${isDarkMode ? "bg-slate-900" : "bg-white"} shadow-[0_22px_50px_-34px_rgba(15,23,42,0.7)]`
    : `${isDarkMode ? "border-slate-700 bg-slate-900 hover:border-slate-500" : "border-slate-200 bg-white hover:border-slate-400"}`;
  const actionClass = selected
    ? isDarkMode ? tone.darkAction : tone.action
    : isDarkMode ? tone.darkOutline : tone.outline;
  const toneSurface = isDarkMode ? tone.darkSurface : tone.surface;
  const toneText = isDarkMode ? tone.darkText : tone.text;
  const PlanIcon = PLAN_ICONS[plan.value] || FaBriefcase;

  return (
    <button
      type="button"
      aria-pressed={selected}
      aria-label={`Select ${plan.name} plan`}
      onClick={() => onSelect(plan.value)}
      className={`group relative flex min-h-[460px] w-full flex-col overflow-hidden border text-left transition duration-200 focus:outline-none ${cardClass}`}
    >
      <span className={`absolute inset-x-0 top-0 h-1 ${tone.bar}`} aria-hidden="true" />

      <span className={`block border-b px-6 pb-6 pt-7 ${toneSurface} ${isDarkMode ? "border-slate-700" : "border-slate-200"}`}>
        <span className="flex min-h-[25px] justify-end">
          {plan.badge && (
            <span className={`shrink-0 border px-2.5 py-1 text-[11px] font-bold ${isDarkMode ? `${tone.darkBorder} ${tone.darkText}` : `${tone.border} ${tone.text}`}`}>
              {plan.badge}
            </span>
          )}
        </span>
        <span className="mt-4 flex min-w-0 items-center gap-3">
          <span className={`flex h-11 w-11 shrink-0 items-center justify-center border ${isDarkMode ? `${tone.darkBorder} ${tone.darkSurface} ${tone.darkText}` : `${tone.border} ${tone.surface} ${tone.text}`}`}>
            <PlanIcon aria-hidden="true" />
          </span>
          <span className={`min-w-0 text-xs font-bold uppercase tracking-[0.15em] ${toneText}`}>{plan.audience}</span>
        </span>
        <span className={`mt-4 block text-[1.7rem] font-semibold tracking-tight ${headingClass}`}>{plan.name}</span>
        <span className={`mt-3 block min-h-[48px] max-w-[16rem] text-sm leading-6 ${bodyClass}`}>{plan.description}</span>
      </span>

      <span className="block px-6">
        <span className={`mt-6 grid min-h-[104px] grid-cols-[minmax(0,1fr)_auto] items-center gap-4 border p-4 ${toneSurface} ${isDarkMode ? tone.darkBorder : tone.border}`}>
          <span className={`whitespace-nowrap text-[2.35rem] font-semibold leading-none tracking-tight ${headingClass}`}>{formatInr(plan.price)}</span>
          <span className={`pb-0.5 text-right text-xs leading-5 ${mutedClass}`}>
            {plan.duration.toLowerCase()}
            <br />
            billed once
          </span>
        </span>

        <span className={`mt-4 block w-full border px-4 py-3 text-center text-sm font-semibold transition ${actionClass}`}>
          {selected ? "Selected plan" : `Choose ${plan.name}`}
        </span>
      </span>

      <span className={`mx-6 mt-6 block border-t pt-5 ${dividerClass}`}>
        <span className={`block text-xs font-bold uppercase tracking-[0.14em] ${mutedClass}`}>Included features</span>
        <span className="mt-4 block space-y-3">
          {plan.features.map((feature) => (
            <span key={feature} className={`flex items-start gap-3 text-sm leading-5 ${bodyClass}`}>
              <FaCheck className={`mt-1 shrink-0 ${toneText}`} aria-hidden="true" />
              <span>{feature}</span>
            </span>
          ))}
        </span>
      </span>

      <span className={`mx-6 mt-auto flex items-center justify-between gap-3 border-t py-5 ${dividerClass}`}>
        <span className={`text-xs font-semibold ${mutedClass}`}>Billing benefit</span>
        <span className={`text-xs font-bold ${toneText}`}>{plan.savings}</span>
      </span>
    </button>
  );
};

export default SubscriptionPlanCard;
